import type { IErrorReportContext } from './error-reporter.service';
import type { INormalizedHttpError } from './http-error.model';
import { HttpErrorKind } from './http-error.model';

/** Numeric ids would otherwise split one bug into one GlitchTip issue per resource. */
const ID_SEGMENT = /\/\d+(?=\/|$)/g;

/**
 * Expected on their own — a resource the user may not see, a stale row, a rejected form, a rate
 * limit, a dropped connection — so none of the six is reported, and a read's 403/404 is not even
 * shown. That leaves a systematic one invisible: a permission that broke for every request, every
 * save rejected since a deploy, a client stuck in a 409 loop, or a network that never comes back.
 * Only a write's 400 gets this far; a read's is reported outright as a bad query.
 */
const WATCHED_KINDS = new Set([
  HttpErrorKind.Forbidden,
  HttpErrorKind.NotFound,
  HttpErrorKind.Validation,
  HttpErrorKind.Conflict,
  HttpErrorKind.RateLimited,
  HttpErrorKind.Offline,
]);

/** Two or three of a kind is a page doing its job; eight inside half a minute is not. */
const BURST_THRESHOLD = 8;
const BURST_WINDOW_MS = 30_000;

interface IBurst {
  startedAt: number;
  count: number;
  paths: Set<string>;
}

const bursts = new Map<HttpErrorKind, IBurst>();

const pathKeyOf = (url: string): string => (url.split('?')[0] ?? url).replace(ID_SEGMENT, '/:id');

export const contextFor = (httpError: INormalizedHttpError | undefined): IErrorReportContext | undefined => {
  if (httpError === undefined) {
    return undefined;
  }
  const status = String(httpError.status);
  return {
    fingerprint: ['http', httpError.method, status, pathKeyOf(httpError.url)],
    tags: { 'error.kind': httpError.kind, 'http.status': status, 'http.method': httpError.method },
  };
};

/**
 * Counts the failures nobody reports, per kind, and answers with a context once there have been
 * enough of them to be a defect rather than a fact. The distinct-path count is the triage: one
 * path is an endpoint looping, twenty is the whole app. Escalating clears the window, so one
 * event describes each burst.
 */
export const systematicFailureContext = (httpError: INormalizedHttpError): IErrorReportContext | undefined => {
  if (!WATCHED_KINDS.has(httpError.kind)) {
    return undefined;
  }
  const now = performance.now();
  const burst = bursts.get(httpError.kind);
  if (burst === undefined || now - burst.startedAt > BURST_WINDOW_MS) {
    bursts.set(httpError.kind, { startedAt: now, count: 1, paths: new Set([pathKeyOf(httpError.url)]) });
    return undefined;
  }

  burst.count += 1;
  burst.paths.add(pathKeyOf(httpError.url));
  if (burst.count < BURST_THRESHOLD) {
    return undefined;
  }

  bursts.delete(httpError.kind);
  return {
    fingerprint: ['http-burst', httpError.kind],
    tags: {
      'error.kind': httpError.kind,
      'burst.count': String(burst.count),
      'burst.paths': String(burst.paths.size),
    },
  };
};
